import { type FormEvent, useCallback } from 'react';

import type { FetcherWithComponents } from '@remix-run/react';
import { CardElement } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';

interface ReturnProps {
  handleSubmit: (
    event: FormEvent,
    fetcher: FetcherWithComponents<unknown>,
    stripeSession: Stripe | null,
    elements: StripeElements | null,
    email: string,
  ) => Promise<void>;
}

const useGiftOrGroupCheckout = (isInternalCheckout: boolean, cardProcessingErrorMessage: string): ReturnProps => {
  const handleSubmit = useCallback(
    async (
      event: FormEvent,
      fetcher: FetcherWithComponents<unknown>,
      stripeSession: Stripe | null,
      elements: StripeElements | null,
      email: string,
    ) => {
      const formData = new FormData(event.target as HTMLFormElement);

      if (isInternalCheckout && elements && stripeSession) {
        const card = elements.getElement(CardElement);
        if (!card) {
          throw new Error(cardProcessingErrorMessage);
        }

        const result = await stripeSession.createToken(card);

        if (result.error) {
          throw new Error(result.error?.message || cardProcessingErrorMessage);
        }

        const paymentMethod = await stripeSession.createPaymentMethod({
          type: 'card',
          card,
          billing_details: {
            email,
          },
        });

        if (paymentMethod?.paymentMethod?.id) {
          formData.append('payment_method', paymentMethod.paymentMethod.id);
        }
      }

      fetcher.submit(formData, { method: 'post', action: '/upgrade' });
    },
    [isInternalCheckout, cardProcessingErrorMessage],
  );

  return {
    handleSubmit,
  };
};

export default useGiftOrGroupCheckout;
